import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DEFAULT_DELAY } from "./useDebounce";
import { UseListFiltersReturn } from "./useListFilters";

// Owns its own search term + URL param entirely independently of useListFilters' q/setSearchTerm
// (which stay reserved for Table's built-in search box). filters is only used to reset pagination
// back to page 1 when a new search commits.
export const useFilterSearch = (
  filters: UseListFiltersReturn,
  aborter: React.RefObject<AbortController>,
  key: string,
  delay: number = DEFAULT_DELAY,
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Deliberately namespaced apart from useListFilters' own "q_<key>" — this hook owns its
  // search term independently and never touches useListFilters' q/setSearchTerm.
  const qParamName = `search_${key}`;

  const [searchTerm, setSearchTermState] = useState(searchParams.get(qParamName) ?? "");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync FROM the URL only (e.g. browser back/forward navigation) — never writes.
  useEffect(() => {
    const urlValue = searchParams.get(qParamName) ?? "";
    if (urlValue !== searchTerm) {
      setSearchTermState(urlValue);
    }
  }, [searchParams]);

  const handleSearchChange = (term: string) => {
    setSearchTermState(term);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      aborter.current.abort();
      aborter.current = new AbortController();
      filters.setPage(1);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (term) {
          next.set(qParamName, term);
        } else {
          next.delete(qParamName);
        }
        return next;
      });
    }, delay);
  };

  return { searchTerm, handleSearchChange };
};
